import * as React from "react";
import { Modal } from "../../../ui/components/Modal";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { useI18n } from "../../lib/i18n";
import type { CommandExecution } from "../../lib/api/commands";
import {
  CommandDangerPill,
  CommandStatusPill,
  commandSourceLabel,
  formatCommandTime,
  useRelativeElapsed,
} from "./CommandPills";
import { CommandResponseView, JsonBlock } from "./CommandResponseView";
import * as s from "./commands.css";

function TimelineStep({ done, label, at }: { done: boolean; label: string; at?: string | null }) {
  return (
    <div className={`${s.timelineItem}${done ? ` ${s.timelineItemDone}` : ""}`}>
      <div>{label}</div>
      {at && <div className={s.subtle}>{formatCommandTime(at)}</div>}
    </div>
  );
}

export function CommandExecutionModal({
  execution,
  onClose,
  showProduct = false,
}: {
  execution: CommandExecution | null;
  onClose: () => void;
  showProduct?: boolean;
}) {
  const { t } = useI18n();
  const [showRaw, setShowRaw] = React.useState(false);
  const pending = execution?.status === "sent";
  const elapsed = useRelativeElapsed(execution?.sentAt ?? execution?.createdAt, pending);

  React.useEffect(() => setShowRaw(false), [execution?.id]);

  if (!execution) return null;
  const finished = execution.status !== "sent";
  const hasPreRead = execution.preReadPayload != null;

  return (
    <Modal open setOpen={(open) => !open && onClose()} minWidth="520px" maxWidth="760px" closeOnClickOff>
      <div className={s.modalBody}>
        <div className={s.commandCardHeader}>
          <div>
            <Text level="h2" weight="bold">
              {execution.displayName ?? execution.commandKey}
            </Text>
            <span className={s.subtle}>
              {execution.commandKey}
              {showProduct && execution.productName ? ` · ${execution.productName}` : ""}
            </span>
          </div>
          <div className={s.inlineRow}>
            <CommandDangerPill level={execution.dangerLevel} />
            <CommandStatusPill status={execution.status} phase={execution.phase} />
          </div>
        </div>

        <div className={s.kvGrid}>
          <span className={s.kvKey}>{t("pro.commands.detail.sentBy")}</span>
          <span>
            {execution.actorName ?? "—"} · {commandSourceLabel(t, execution.source)}
          </span>
          {execution.topic && (
            <>
              <span className={s.kvKey}>{t("pro.commands.detail.topic")}</span>
              <span className={s.subtle}>{execution.topic}</span>
            </>
          )}
        </div>

        <div className={s.timeline}>
          <TimelineStep
            done
            label={t("pro.commands.detail.step.sent")}
            at={execution.sentAt ?? execution.createdAt}
          />
          {hasPreRead && (
            <TimelineStep
              done={execution.preReadResult != null}
              label={t("pro.commands.detail.step.preRead")}
            />
          )}
          <TimelineStep
            done={finished}
            label={
              finished
                ? t(`pro.commands.detail.step.${execution.status}`)
                : t("pro.commands.detail.step.waiting", { seconds: elapsed })
            }
            at={execution.respondedAt ?? (finished ? execution.updatedAt : null)}
          />
        </div>

        {execution.error && <div className={s.bannerDanger}>{execution.error}</div>}

        {finished && (
          <div className={s.field}>
            <span className={s.sectionLabel}>{t("pro.commands.detail.response")}</span>
            <CommandResponseView execution={execution} />
          </div>
        )}

        <div>
          <button type="button" className={s.linkButton} onClick={() => setShowRaw((v) => !v)}>
            {showRaw ? t("pro.commands.detail.hideRaw") : t("pro.commands.detail.showRaw")}
          </button>
        </div>
        {showRaw && (
          <>
            {execution.params && Object.keys(execution.params).length > 0 && (
              <div className={s.field}>
                <span className={s.sectionLabel}>{t("pro.commands.detail.params")}</span>
                <JsonBlock value={execution.params} />
              </div>
            )}
            {hasPreRead && (
              <div className={s.field}>
                <span className={s.sectionLabel}>{t("pro.commands.detail.preReadRequest")}</span>
                <JsonBlock value={execution.preReadPayload} />
              </div>
            )}
            <div className={s.field}>
              <span className={s.sectionLabel}>{t("pro.commands.detail.request")}</span>
              <JsonBlock value={execution.requestPayload} />
            </div>
            <div className={s.field}>
              <span className={s.sectionLabel}>{t("pro.commands.detail.rawResponse")}</span>
              <JsonBlock value={execution.responsePayload} />
            </div>
          </>
        )}

        <div className={s.modalFooter}>
          <Button type="button" color="neutral" onClick={onClose}>
            {t("shared.common.close")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
